import React from "react";
import { Box, Typography } from "@mui/material";
import {
  lineIdentity,
  isIdentityEmpty,
  formatVoltage,
  formatDistance,
} from "../powerLineIdentity";

/**
 * Voltage and circuit identity for the line passing nearest a node (#209).
 *
 * Sits above the sensor list because it answers a question that comes before any
 * reading does: which asset is this, at what voltage, and who has to be called
 * before anyone climbs. "Node 2" appears in no document the operator holds;
 * "110 kV" does.
 *
 * TWO RULES, BOTH ABOUT NOT GUESSING
 *
 * 1. Every field is drawn only if the matched feature carries it. OSM coverage
 *    across the 1,254 features is thin and uneven — 755 have a voltage, 44 a
 *    name, 27 an operator — and the feature the three pilot nodes match carries
 *    voltage and frequency and nothing else. So in production today this renders
 *    "110 kV · 50 Hz" and no name, which is the correct output, not a bug. A
 *    guessed voltage next to a tower is worse than no voltage at all.
 *
 * 2. The match is proximity, never membership. The distance line is always
 *    drawn, for every node, so nobody reads a nearest-line match as a record of
 *    what this tower is wired to. For a node the operator has explicitly ruled
 *    off the corridor (off_corridor_reason in SamstNodes) that caveat is spelled
 *    out in full: one such node sits 0 m from a mapped line it is not on, so
 *    distance alone would have labelled it with full confidence. This is the
 *    same rule #136 set for segment_order, applied to a weaker claim.
 */
export default function NodeLineIdentity({ match, offCorridor = false, t }) {
  // No line within the threshold. Nothing is drawn rather than an empty row —
  // "we did not find one" is not a fact worth a line of the panel.
  if (!match) return null;

  const identity = lineIdentity(match.properties);
  // A feature carrying only `power=line` matches as well as any other and says
  // nothing. 499 of the 1,254 features have no voltage at all.
  if (isIdentityEmpty(identity)) return null;

  const voltage = formatVoltage(identity.voltagesKv);
  const title = identity.name ?? identity.ref;

  // Secondary facts, in the order an engineer asks for them. Each is dropped
  // entirely when the source is silent, so the row shrinks rather than showing
  // a label with nothing after it.
  const details = [];
  if (identity.circuits !== null)
    details.push(
      identity.circuits === 1
        ? t("lineCircuit")
        : t("lineCircuits", { n: identity.circuits })
    );
  if (identity.operator !== null)
    details.push(t("lineOperator", { name: identity.operator }));
  if (identity.cables !== null)
    details.push(
      identity.cables === 1 ? t("lineCable") : t("lineCables", { n: identity.cables })
    );
  if (identity.frequencyHz !== null) details.push(`${identity.frequencyHz} Hz`);

  return (
    <Box sx={{ mt: 1.5, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t("lineIdentity")}
      </Typography>

      {offCorridor && (
        <Typography variant="body2" sx={{ color: "#92400e", mt: 0.25 }}>
          {t("lineNotOnCorridor")}
        </Typography>
      )}

      {(voltage || title) && (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", mt: 0.25 }}>
          {voltage && (
            <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>{voltage}</Typography>
          )}
          {title && <Typography variant="body2">{title}</Typography>}
        </Box>
      )}

      {details.length > 0 && (
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
          {details.join(" · ")}
        </Typography>
      )}

      {/* Always drawn, on every node. This is the line that stops a proximity
          match being read as the asset register. */}
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
        {t("lineSource", { distance: formatDistance(match.distanceM) })}
      </Typography>
    </Box>
  );
}
